import {useMutation} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';
import {showHttpErrorToast} from '@common/http/errors/show-http-error-toast';
import {useLocation} from 'react-router';

export type CountrySurface = 'website' | 'user_panel' | 'admin' | 'api';

export type SiteCountry = {
  id?: number;
  code: string;
  name: string;
  native_name?: string;
  currency: string;
  symbol: string;
  language?: string | null;
  enabled?: boolean;
  visibility?: Record<CountrySurface, boolean>;
  /** null = follow the site gateway setting */
  gateways?: Record<string, boolean | null>;
  /** Build 131: 'global' = no overrides; 'custom' = gateway_list in order. */
  gateway_mode?: 'global' | 'custom';
  gateway_list?: string[];
  sort_order?: number;
};

export type ResolvedGateways = {
  stripe: boolean;
  paypal: boolean;
  /** Gateways that are on, in checkout order (build 131). */
  order?: string[];
  source: 'country' | 'global';
  country: string | null;
  [gateway: string]: unknown;
};

/** Same rules as CountryCatalog::surfaceForPath on the server. */
export function surfaceForPath(pathname: string): CountrySurface {
  if (/^\/admin(\/|$)/.test(pathname)) return 'admin';
  if (
    /^\/(drive|account-settings|workspace|workspaces|notifications|billing|api-keys)(\/|$)/.test(
      pathname,
    )
  ) {
    return 'user_panel';
  }
  return 'website';
}

export function useMultiCountriesEnabled(): boolean {
  return useBootstrapDataStore(s => Boolean(s.data.country?.enabled));
}

/** Countries visible on the surface of the current route. Empty when multi-countries is off. */
export function useSiteCountries(): SiteCountry[] {
  const {pathname} = useLocation();
  const surface = surfaceForPath(pathname);
  const enabled = useMultiCountriesEnabled();
  const countries = useBootstrapDataStore(s => s.data.country?.countries);
  if (!enabled || !countries) return [];
  return countries.filter(
    country => country.enabled !== false && country.visibility?.[surface] !== false,
  );
}

/** Selected country for this surface, or null = global. */
export function useActiveCountry(): SiteCountry | null {
  const active = useBootstrapDataStore(s => s.data.country?.active ?? null);
  const countries = useSiteCountries();
  if (!active) return null;
  return countries.find(country => country.code === active.code) ?? null;
}

export function useResolvedGateways(): ResolvedGateways | null {
  return useBootstrapDataStore(s => s.data.country?.gateways ?? null);
}

export function useChangeCountry() {
  return useMutation({
    mutationFn: (code: string | null) =>
      apiClient
        .post('regions/country', {country: code ?? 'GLOBAL'})
        .then(r => r.data as {country: SiteCountry | null}),
    onSuccess: () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('country');
      window.location.assign(url.pathname + url.search + url.hash);
    },
    onError: err => showHttpErrorToast(err),
  });
}
